import { cn } from '@/utils/tailwind';
import React, { forwardRef } from 'react';
import { OrderCollectingOverlay } from '../_components/common/OrderCollectingOverlay';
import { useOrderDetails } from '../_components/order-details/OrderDetailsProvider';
import { useOrderStickyLayout } from '../_contexts/OrderStickyLayoutContext';

interface LayoutOrderContainerProps {
  children: React.ReactNode;
  className?: string;
}

// ref는 forwardRef의 두 번째 인자로만 받으면 됩니다.
export const LayoutOrderContainer = forwardRef<HTMLDivElement, LayoutOrderContainerProps>(
  ({ children, className = '' }, ref) => {
    return (
      <div
        ref={ref ? ref : undefined}
        className={cn('relative flex min-h-full w-full min-w-0 flex-col', className)}
      >
        {children}
      </div>
    );
  },
);

LayoutOrderContainer.displayName = 'LayoutOrderContainer';

export const LayoutTabSection = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex flex-col    bg-gr-50 pt-[40px] ">
      <div className="flex w-full min-w-0 max-w-full flex-col px-[60px]">{children}</div>
    </div>
  );
};

export const LayoutTables = ({
  children,
  isTableBorder = false,
}: {
  children: React.ReactNode;
  isTableBorder?: boolean;
}) => {
  const { isCollectingOrders } = useOrderDetails();

  // flex-1 로 남은 세로 공간을 흡수해, 결과가 짧거나 비어도 흰 배경이 푸터 위까지 채워지게 한다.
  // 카드 상단부(border-t, rounded-t)는 sticky 영역의 LayoutTableController 가 담당하므로
  // 여기는 카드 본문: border-x/b 만 그려 위와 이어붙인다. 테이블은 Figma 처럼 좌우 16px 인셋.
  return (
    <div className="flex w-full min-w-0 flex-1 flex-col bg-white px-[60px] pb-[36px]">
      <div
        className={cn(
          // flex-1: 빈/짧은 결과에서 테이블 박스를 흰 영역 전체로 늘려 empty 상태가 중앙에 오게 한다.
          'relative flex w-full min-w-0 max-w-full flex-1 flex-col rounded-b-[20px]',
          isTableBorder && 'border-x border-b border-gr-200 px-[16px] pb-[16px]',
        )}
      >
        {children}
        {isCollectingOrders && <OrderCollectingOverlay />}
      </div>
    </div>
  );
};

export const LayoutStickySection = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  const { stickyControlsTop } = useOrderStickyLayout();

  // 높이/패딩/간격을 --order-compact(0~1)로 보간한다. 모든 값이 동일 변수에서 계산되고
  // table split header 의 top 도 같은 변수를 쓰므로 둘이 lockstep 으로 움직여 떨림이 없다.
  return (
    <div
      className={cn(
        'sticky z-[35] flex w-full min-w-0 max-w-full flex-col overflow-hidden bg-white px-[60px] backdrop-blur-sm',
        className,
      )}
      style={{
        top: `var(--order-sticky-controls-top, ${stickyControlsTop}px)`,
        height: 'var(--order-controls-height)',
        paddingTop: 'var(--order-controls-pt)',
        paddingBottom: 'var(--order-controls-pb)',
        rowGap: 'var(--order-controls-gap)',
      }}
    >
      {children}
    </div>
  );
};

export const LayoutReviewBodyContent = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex w-full flex-1 flex-col  bg-white  pb-[36px]">
      <div
        className={`flex  w-full max-w-full  flex-1 flex-col rounded-[20px] 
        `}
      >
        {children}
      </div>
    </div>
  );
};
